import { createContext, useContext, type ReactNode } from 'react';

import { cn } from '../lib/cn.js';

import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip.js';

const ColumnHintsContext = createContext(true);

/** Org layout sets this from the display setting. Defaults to on so
 *  tables outside that layout still explain their columns. */
export function ColumnHintsProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}) {
  return <ColumnHintsContext.Provider value={enabled}>{children}</ColumnHintsContext.Provider>;
}

export function useColumnHints(): boolean {
  return useContext(ColumnHintsContext);
}

/** Hover explanation for a table heading. Renders the heading alone when
 *  there is no description or the org has turned explanations off. */
export function ColumnHint({
  description,
  children,
}: {
  description?: string;
  children: ReactNode;
}) {
  const enabled = useColumnHints();
  if (!enabled || !description) return <>{children}</>;
  const underline = typeof children === 'string' && children.trim().length > 0;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex max-w-full cursor-help',
            underline && 'underline decoration-dotted decoration-fg-muted/70 underline-offset-4',
          )}
        >
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs whitespace-normal text-left font-normal normal-case tracking-normal">
        {description}
      </TooltipContent>
    </Tooltip>
  );
}
